import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api, ApiError } from '../../api/client';
import { AppIcon } from '../../components/AppIcon';
import { ExportButton } from '../../components/ExportButton';
import { DateRangeFilter, useDateFilter } from '../../components/DateRangeFilter';
import { Alert, Card, EmptyState, PageHeader, Spinner } from '../../components/ui/display';
import type { ProfitAndLoss } from '../../types/models';
import { formatDate, formatMoney } from '../../utils/format';
import { SegmentedToggle } from './PnlReportPage';
import { compactInr, PnlSummary, PnlTable } from './pnlParts';

/**
 * App performance: every app's P&L for the range, best first, plus the
 * company-level expenses that are not tied to an app. App rows plus the
 * company-level row add up to the total.
 */
export function AppPerformancePage() {
  const [range, setRange] = useDateFilter('thisMonth');
  const [view, setView] = useState<'actual' | 'estimated'>('actual');
  const query = useQuery({
    queryKey: ['pnl', range.startDate, range.endDate],
    queryFn: () =>
      api.get<ProfitAndLoss>('/pnl', { startDate: range.startDate, endDate: range.endDate }),
    placeholderData: keepPreviousData,
  });
  const d = query.data;
  const pick = <T extends { estimated: unknown; actual: unknown }>(x: T) =>
    (view === 'actual' ? x.actual : x.estimated) as T['actual'];
  const apps = d
    ? [...d.apps].sort((a, b) => Number(pick(b).operatingProfit) - Number(pick(a).operatingProfit))
    : [];
  const chart = apps.map((a) => ({ name: a.app.name, profit: Number(pick(a).operatingProfit) }));

  return (
    <>
      <PageHeader
        title="App performance"
        description={`${formatDate(range.startDate)} – ${formatDate(range.endDate)} · operating profit per app (revenue − marketing − other expenses, before GST)`}
        actions={
          <>
            <ExportButton
              report="app-performance"
              params={{ startDate: range.startDate, endDate: range.endDate, view }}
            />
            <DateRangeFilter value={range} onChange={setRange} />
          </>
        }
      />
      <div className="mb-4">
        <SegmentedToggle
          label="Figures"
          value={view}
          onChange={setView}
          options={[
            { value: 'actual', label: 'After month-end actuals' },
            { value: 'estimated', label: 'Estimated (daily entries)' },
          ]}
        />
      </div>
      {query.isError && (
        <Alert tone="error">
          {query.error instanceof ApiError ? query.error.message : 'Failed to load'}
        </Alert>
      )}
      {query.isLoading && <Spinner />}
      {d && (
        <>
          <PnlSummary f={pick(d)} revenueForeign={d.revenueForeign} />
          {apps.length === 0 && !d.companyLevel ? (
            <Card>
              <EmptyState title="No data in this period" />
            </Card>
          ) : (
            <>
              <Card title="Operating profit by app" className="mb-6">
                <div style={{ height: Math.max(160, chart.length * 44 + 40) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chart}
                      layout="vertical"
                      margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                      <XAxis
                        type="number"
                        tickFormatter={compactInr}
                        tick={{ fontSize: 11, fill: '#64748b' }}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={160}
                        tick={{ fontSize: 12, fill: '#334155' }}
                      />
                      <Tooltip formatter={(v) => [formatMoney(String(v)), 'Operating profit']} />
                      <Bar dataKey="profit" radius={[0, 3, 3, 0]} maxBarSize={28}>
                        {chart.map((c) => (
                          <Cell key={c.name} fill={c.profit < 0 ? '#e11d48' : '#10b981'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
              <Card bodyClassName="p-0">
                <PnlTable
                  firstHeader="App"
                  rows={[
                    ...apps.map((a) => ({
                      key: a.app.id,
                      label: (
                        <Link
                          to={`/apps/${a.app.id}`}
                          className="flex items-center gap-2 font-medium text-slate-900 hover:text-brand-700"
                        >
                          <AppIcon src={a.app.iconUrl} name={a.app.name} size={24} />
                          {a.app.name}
                        </Link>
                      ),
                      f: pick(a),
                      foreign: a.revenueForeign,
                      adjusted:
                        view === 'actual' &&
                        a.actual.operatingProfit !== a.estimated.operatingProfit,
                    })),
                    ...(d.companyLevel
                      ? [
                          {
                            key: 'company',
                            label: (
                              <span className="flex items-center gap-2 text-slate-500">
                                <Building2 className="size-4" /> Company-level expenses
                              </span>
                            ),
                            f: d.companyLevel,
                          },
                        ]
                      : []),
                  ]}
                  totalForeign={d.revenueForeign}
                  total={pick(d)}
                />
              </Card>
            </>
          )}
        </>
      )}
    </>
  );
}
